import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  NativeSelect,
  NumberInput,
  Separator,
  Switch,
  Text,
  VStack,
  useToken
} from "@chakra-ui/react"
import {
  getAlphaColor,
  parseRgba,
  stringifyRgba
} from "@pathwright/ui/src/components/utils/colors"
import ColorPicker from "@pathwright/ux/src/components/color/ColorPicker"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { forwardRef, useMemo, useState } from "react"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canEditLibrary } from "../../user/permissions"
import { useCertificateContext } from "../context/CertificateContext"
import { useCertificateState } from "../context/CertificateState"
import fonts, {
  getFont,
  getFontByFontName,
  getFontName,
  parseFontName
} from "../fonts/fonts"
import useCertificatePreview from "../hooks/useCertificatePreview"
import useCertificateSyncPreview from "../hooks/useCertificateSyncPreview"
import CertificateTemplateForm from "../template/CertificateTemplateForm"
import { createDefaultTextObject } from "../text/CertificateText"

const Info = () => {
  const { certificateScope } = useCertificateContext()
  const pwContext = usePathwrightContext()

  if (canEditLibrary(pwContext) && certificateScope) {
    return (
      <VStack gap={4} alignItems="flex-start" width="100%" boxShadow="lg">
        <Box bg={"white"} p={4} borderRadius={"md"}>
          To modify the design of all certificates visit the{" "}
          <Link to="/certificate/" variant="underline">
            Certificate Template Editor
          </Link>
          .
        </Box>
      </VStack>
    )
  }

  return null
}

const BackgroundSection = () => (
  <VStack
    gap={4}
    alignItems="flex-start"
    width="100%"
    css={{
      "& .CertificateTemplateForm": {
        maxW: 250,
        alignSelf: "center"
      }
    }}
  >
    <Heading as="h5" size="md" mb={2}>
      Background
    </Heading>
    <CertificateTemplateForm hash="demo" />
  </VStack>
)

const TextObjectFont = ({ textObject, onChangeTextObject }) => {
  const selectedFont = getFontByFontName(textObject.font_name)
  const selectedFontVariants = selectedFont ? selectedFont.variants : []
  const { fontVariant: selectedFontVariant } = parseFontName(
    textObject.font_name
  )

  const handleChangeFontName = (e) => {
    const fontKey = e.target.value
    const fontVariant = getFont(fontKey).variants.reduce(
      (matchingFontVariant, fontVariant) =>
        fontVariant === selectedFontVariant
          ? selectedFontVariant
          : matchingFontVariant,
      "regular"
    )
    onChangeTextObject({
      font_name: getFontName(fontKey, fontVariant)
    })
  }

  const fontVariantIcons = {
    bold: "bold",
    italic: "italic"
  }

  const FontVariantButton = ({ fontVariant }) =>
    fontVariant !== "regular" ? (
      <IconButton
        aria-label={`Certificate font variant ${fontVariant}`}
        colorPalette="blackAlpha"
        variant={fontVariant === selectedFontVariant ? "solid" : "ghost"}
        w={8}
        h={8}
        minW={8}
        minH={8}
        children={<Pathicon icon={fontVariantIcons[fontVariant]} />}
        onClick={() => {
          const nextFontVariant =
            fontVariant === selectedFontVariant ? "regular" : fontVariant
          const fontName = getFontName(selectedFont.key, nextFontVariant)
          onChangeTextObject({ font_name: fontName })
        }}
      />
    ) : null

  return (
    <HStack gap={4}>
      <Text mb={0}>Font</Text>
      <NativeSelect.Root
        icon={<Pathicon icon="chevron-down" />}
        placeholder={!selectedFont && (textObject.font_name || "Select font")}
        value={selectedFont && selectedFont.key}
        onChange={handleChangeFontName}
      >
        <NativeSelect.Field>
          {fonts.map((fontGroup) => (
            <optgroup key={fontGroup.category} label={fontGroup.category}>
              {fontGroup.fonts.map((font) => (
                <option key={font.key} value={font.key}>
                  {font.name}
                </option>
              ))}
            </optgroup>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      {selectedFontVariants.map((fontVariant) => (
        <FontVariantButton key={fontVariant} fontVariant={fontVariant} />
      ))}
    </HStack>
  )
}

const TextObjectFontSize = ({ textObject, onChangeTextObject }) => {
  const min = 12
  const max = 48

  return (
    <HStack gap={4}>
      <Text mb={0}>Size</Text>
      <NumberInput.Root
        defaultValue={textObject.font_size + ""}
        onValueChange={({ value }) => {
          const parsedValue = Math.min(
            Math.max(parseFloat(value) || 0, min),
            max
          )
          // Only setting the value when valid. Allowing the internal state to
          // use string value while setting the external state to a parsed float.
          if (parseFloat(value) === parsedValue) {
            onChangeTextObject({ font_size: parsedValue })
          }
        }}
        step={1}
        min={min}
        max={max}
      >
        <NumberInput.Input />
        <NumberInput.Control>
          <NumberInput.IncrementTrigger
            children={<Pathicon icon="chevron-up" />}
          />
          <NumberInput.DecrementTrigger
            children={<Pathicon icon="chevron-down" />}
          />
        </NumberInput.Control>
      </NumberInput.Root>
    </HStack>
  )
}

const SwatchButton = forwardRef(
  (
    {
      color,
      index,
      selectedColor,
      selectedColorIndex,
      setSelectedColorIndex,
      onClick,
      children,
      ...rest
    },
    ref
  ) => (
    <Button
      ref={ref}
      {...rest}
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="100%"
      color="white"
      bgColor={color}
      onClick={(e) => {
        onClick?.(e)
        setSelectedColorIndex(index)
      }}
      _hover={{ bg: getAlphaColor(color, 0.8) }}
      _active={{ bg: getAlphaColor(color, 0.7) }}
      w={8}
      h={8}
      minW={8}
      minH={8}
      p={0}
      css={
        stringifyRgba(color) === stringifyRgba(selectedColor) &&
        (isNaN(selectedColorIndex) || selectedColorIndex === index) && {
          "&:before": {
            content: '""',
            borderRadius: "inherit",
            border: "2px solid white",
            margin: "2px",
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0"
          }
        }
      }
    >
      {children}
    </Button>
  )
)

const TextObjectColor = ({ textObject, onChangeTextObject }) => {
  // Get the raw CSS value of the primary brand color.
  const [brand600] = useToken("colors", ["brand.600"])

  const defaultColors = [
    "0,0,0,100", // black
    "100,100,100", // dark gray
    stringifyRgba(brand600) // school primary brand color
  ]
    .filter(Boolean)
    .map((color) => parseRgba(color))

  const selectedColor = useMemo(
    () => parseRgba(textObject.font_color),
    [textObject.font_color]
  )

  // Track in state what color is currently selected. Cannot simply use
  // color value as the selected color for the color picker could match
  // any of the default colors, which would result in showing both colors
  // as selected.
  const [selectedColorIndex, setSelectedColorIndex] = useState()

  // Set the picker color to the textObject.font_color when that value doesn't
  // match any of the defaultColors.
  const [pickerColor, setPickerColor] = useState(
    selectedColor && !defaultColors.find((color) => color === selectedColor)
      ? selectedColor
      : "black"
  )

  const handlePickerColor = (color) => {
    const stringifiedRgb = stringifyRgba(color)
    setPickerColor(color)
    onChangeTextObject({
      font_color: stringifiedRgb
    })
  }

  return (
    <HStack gap={4}>
      <Text mb={0}>Color</Text>
      {defaultColors.map((color, i) => (
        <SwatchButton
          key={color}
          color={color}
          index={i}
          onClick={() =>
            onChangeTextObject({ font_color: stringifyRgba(color) })
          }
          selectedColor={selectedColor}
          selectedColorIndex={selectedColorIndex}
          setSelectedColorIndex={setSelectedColorIndex}
        />
      ))}

      <ColorPicker
        color={pickerColor}
        initialColor={selectedColor}
        disableAlpha
        onChange={handlePickerColor}
        recent
      >
        <SwatchButton
          color={pickerColor}
          index={defaultColors.length}
          onClick={() => handlePickerColor(pickerColor)}
          selectedColor={selectedColor}
          selectedColorIndex={selectedColorIndex}
          setSelectedColorIndex={setSelectedColorIndex}
        >
          <Pathicon icon="brush" />
        </SwatchButton>
      </ColorPicker>
    </HStack>
  )
}

const TextObjectAlignment = ({ textObject, onChangeTextObject }) => {
  const alignmentIcons = {
    left: "text-align-left",
    center: "text-align-center",
    right: "text-align-right"
  }

  const AlignButton = ({ alignment }) => (
    <IconButton
      aria-label={`Certificate text align ${alignment}`}
      colorPalette="blackAlpha"
      variant={alignment === textObject.alignment ? "solid" : "ghost"}
      w={8}
      h={8}
      minW={8}
      minH={8}
      children={<Pathicon icon={alignmentIcons[alignment]} />}
      onClick={() => onChangeTextObject({ alignment })}
    />
  )

  return (
    <HStack gap={4}>
      <Text mb={0}>Alignment</Text>
      {Object.keys(alignmentIcons).map((alignment) => (
        <AlignButton key={alignment} alignment={alignment} />
      ))}
    </HStack>
  )
}

const TextObjectSection = () => {
  const {
    certificateState: { textObjects },
    selectedTextObjects,
    handleChangeTextObjects,
    selectedTextObjectIndexes
  } = useCertificateState()

  // Change all selected text objects at once.
  const onChangeTextObject = (partialTextObject) => {
    handleChangeTextObjects(
      textObjects.map((textObject, index) =>
        selectedTextObjectIndexes.includes(index)
          ? {
              ...textObject,
              ...partialTextObject
            }
          : textObject
      )
    )
  }

  // Only grab these fields.
  const fields = ["font_name", "font_size", "font_color", "alignment"]

  // Supports multiple selection.
  // When there's only 1 selected text object, we'll simply be left with the
  // same textObject values of that text object.
  const expandedTextObject = selectedTextObjects.reduce((acc, textObject) => {
    Object.entries(textObject).forEach(([key, value]) => {
      if (fields.includes(key)) {
        acc[key] ||= []
        acc[key].push(value)
      }
    })
    return acc
  }, {})

  // All keys that have only 1 unique value retain that value, all others
  // are given an "undefined" value which the inputs will use to determine
  // whether to show an "unselected" state.
  const textObject = Object.entries(expandedTextObject).reduce(
    (acc, [key, value]) => {
      const values = [...new Set([...value])]
      acc[key] = values.length === 1 ? values[0] : undefined
      return acc
    },
    {}
  )

  // Key this component uniquely to the selected indexes in order to reset any form
  // field values.
  const key = selectedTextObjectIndexes.join(":")

  return (
    <VStack key={key} gap={4} alignItems="flex-start" width="100%">
      <Heading as="h5" size="md" mb={2}>
        Text Box
      </Heading>
      <TextObjectFont
        textObject={textObject}
        onChangeTextObject={onChangeTextObject}
      />
      <TextObjectFontSize
        textObject={textObject}
        onChangeTextObject={onChangeTextObject}
      />
      <TextObjectColor
        textObject={textObject}
        onChangeTextObject={onChangeTextObject}
      />
      <TextObjectAlignment
        textObject={textObject}
        onChangeTextObject={onChangeTextObject}
      />
    </VStack>
  )
}

const ProximityLinesSection = () => {
  const { toggleProximityLines, isUsingProximityLines } = useCertificateState()

  return (
    <VStack gap={4} alignItems="flex-start" width="100%">
      <Heading as="h5" size="md" mb={2}>
        Proximiy Lines
      </Heading>
      <Switch.Root
        checked={isUsingProximityLines}
        onCheckedChange={(e) => toggleProximityLines(e.checked)}
      >
        <Switch.HiddenInput />
        <Switch.Label fontWeight="normal">
          Snap to proximity lines when dragging or resizing?
        </Switch.Label>
        <Switch.Control />
      </Switch.Root>
    </VStack>
  )
}

const ActionsSection = () => {
  const { certificate, certificateScope } = useCertificateContext()
  const { certificateState, handleAddTextObject, templateDimensions } =
    useCertificateState()
  const certificatePreview = useCertificatePreview(certificateState)
  const certificateSyncPreview = useCertificateSyncPreview(certificateState)

  return (
    <VStack gap={3} alignItems="flex-start" width="100%">
      <Heading as="h5" size="md" mb={2}>
        Actions
      </Heading>

      {!certificateScope && (
        <HStack gap={2}>
          <Pathicon icon="text-box" />
          <Button
            asChild
            variant="link"
            onClick={() =>
              handleAddTextObject(createDefaultTextObject(templateDimensions))
            }
            disabled={!certificate}
          >
            <Link variant="underline">Add new text box</Link>
          </Button>
        </HStack>
      )}

      <HStack gap={2}>
        <Pathicon icon="eyes" />
        <Button asChild variant="link" disabled={!certificate}>
          <Link href={certificateSyncPreview} variant="underline">
            Live preview
          </Link>
        </Button>
        <Tooltip title="Open a live preview in a new window to see how your changes will look in a real certificate as you make them.">
          <Pathicon icon="info-circle" height={"1em"} />
        </Tooltip>
      </HStack>

      <HStack gap={2}>
        <Pathicon icon="download" />
        <Button asChild variant="link" disabled={!certificate}>
          <Link href={certificatePreview} variant="underline" download={true}>
            Download
          </Link>
        </Button>
      </HStack>

      <HStack gap={2}>
        <Pathicon icon="info-circle" />
        <Link
          href="http://help.pathwright.com/en/articles/9211185-provide-a-certificate-of-completion"
          variant="underline"
        >
          Learn more about Certificates
        </Link>
      </HStack>
    </VStack>
  )
}

const DesignPanel = () => {
  const { certificateScope } = useCertificateContext()
  const { selectedTextObjects } = useCertificateState()
  const hasSelectedTextObject = selectedTextObjects.length >= 1

  return (
    <VStack gap={6} pt={2} pb={4}>
      <Info />
      {!certificateScope && (
        <>
          <BackgroundSection />
          <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
          {!!hasSelectedTextObject && (
            <>
              <TextObjectSection />
              <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
            </>
          )}
          <ProximityLinesSection />
          <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
        </>
      )}
      <ActionsSection />
    </VStack>
  )
}

DesignPanel.displayName = "DesignPanel"

export default DesignPanel
